// components/customBreadcrumb/customBreadcrumb.tsx
import React from 'react';
import { Breadcrumbs, Link, Typography, Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import { Home as HomeIcon, NavigateNext, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumbs, IBreadcrumbItem } from './useBreadcrumbs';
import { useDrawer } from '../components/app/DrawerContext';

interface BreadcrumbProps {
  showHomeIcon?: boolean;
  maxItems?: number;
  variant?: 'default' | 'compact' | 'chip';
}

const CustomBreadcrumb: React.FC<BreadcrumbProps> = ({
  showHomeIcon = false,
  maxItems = 8,
  variant = 'default'
}) => {
  const breadcrumbs = useBreadcrumbs();
  const navigate = useNavigate();
  const theme = useTheme();
  const { setShowAllGroups } = useDrawer();

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleBreadcrumbClick = (path: string, isActive: boolean, name?: string) => {
    if (!isActive && path) {
      // If the breadcrumb is 'Group', set showAllGroups to true
      if (name === 'Group') {
        console.log("clicked on group breadcrumb")
        setShowAllGroups(true);
      }
      navigate(path);
    }
  };

  if (breadcrumbs.length <= 1) {
    return null;
  }

  // Responsive maxItems based on screen size
  const responsiveMaxItems = isMobile ? 2 : isTablet ? 4 : maxItems;

  // Responsive font sizes
  const getFontSize = () => {
    if (isMobile) return 'caption'; // 0.75rem (12px)
    if (isTablet) return 'body2'; // 0.875rem (14px)
    return 'body2'; // 0.875rem (14px)
  };

  // Chip variant for mobile
  if (isMobile && variant === 'chip') {
    return (
      <Box
        sx={{
          padding: { xs: '8px 12px', sm: '12px 16px' },
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #e0e0e0',
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            minWidth: 'fit-content',
            alignItems: 'center'
          }}
        >
          {breadcrumbs.slice(-2).map((item: IBreadcrumbItem, index: number) => {
            const isLast = index === 1;

            return (
              <React.Fragment key={item.path}>
                {index > 0 && (
                  <ChevronRight
                    sx={{
                      fontSize: '1rem',
                      color: 'text.secondary',
                      flexShrink: 0
                    }}
                  />
                )}
                <Chip
                  label={item.name}
                  variant={isLast ? 'filled' : 'outlined'}
                  size="small"
                  clickable={!isLast && !item.isActive}
                  onClick={() => !isLast && handleBreadcrumbClick(item.path, item.isActive)}
                  sx={{
                    fontSize: '0.75rem',
                    height: '24px',
                    maxWidth: '120px',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    },
                    ...(isLast && {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 600
                    }),
                    ...(!isLast && {
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText'
                      }
                    })
                  }}
                />
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Default responsive breadcrumb
  return (
    <Box
      sx={{
        padding: {
          xs: '8px 12px', // Mobile: smaller padding
          sm: '10px 14px', // Tablet: medium padding
          md: '12px 16px' // Desktop: full padding
        },
        backgroundColor: {
          xs: '#f8f9fa', // Mobile: slightly different bg
          sm: '#fafafa' // Tablet+: original bg
        },
        borderBottom: '1px solid #e0e0e0',
        boxShadow: {
          xs: '0 1px 3px rgba(0,0,0,0.1)', // Mobile: subtle shadow
          sm: 'none'
        },
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: { xs: '2px', sm: '4px' }
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '2px'
        }
      }}
    >
      <Breadcrumbs
        aria-label="breadcrumb"
        maxItems={responsiveMaxItems}
        separator={
          <NavigateNext
            fontSize={isMobile ? 'inherit' : 'small'}
            sx={{
              color: '#999',
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}
          />
        }
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: '#666',
            margin: {
              xs: '0 4px', // Mobile: tighter spacing
              sm: '0 6px', // Tablet: medium spacing
              md: '0 8px' // Desktop: full spacing
            }
          },
          '& .MuiBreadcrumbs-ol': {
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            minWidth: { xs: 'fit-content', sm: 'auto' }
          }
        }}
      >
        {breadcrumbs.map((item: IBreadcrumbItem, index: number) => {
          const isLast = index === breadcrumbs.length - 1;
          const isFirst = index === 0;

          // Truncate long names on mobile
          const displayName =
            isMobile && item.name.length > 15 ? `${item.name.substring(0, 12)}...` : item.name;

          if (isLast || item.isActive) {
            return (
              <Typography
                key={item.path}
                variant={getFontSize()}
                color="text.primary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: { xs: 700, sm: 600 }, // Bolder on mobile
                  fontSize: {
                    xs: '0.75rem', // Mobile: caption size
                    sm: '0.8rem', // Tablet: slightly larger
                    md: '0.875rem' // Desktop: body2 size
                  },
                  color: {
                    xs: 'primary.main', // Mobile: primary color
                    sm: 'text.primary' // Others: default color
                  },
                  maxWidth: { xs: '120px', sm: 'none' },
                  overflow: { xs: 'hidden', sm: 'visible' },
                  textOverflow: { xs: 'ellipsis', sm: 'initial' },
                  whiteSpace: { xs: 'nowrap', sm: 'normal' },
                  lineHeight: 1.2
                }}
              >
                {isFirst && showHomeIcon && (
                  <HomeIcon
                    sx={{
                      mr: 0.5,
                      fontSize: { xs: '0.9rem', sm: 'inherit' },
                      color: { xs: 'primary.main', sm: 'inherit' }
                    }}
                  />
                )}
                {displayName}
              </Typography>
            );
          }

          return (
            <Link
              key={item.path}
              component="button"
              underline="hover"
              color="inherit"
              onClick={() => handleBreadcrumbClick(item.path, item.isActive, item.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: {
                  xs: '0.75rem', // Mobile: caption size
                  sm: '0.8rem', // Tablet: slightly larger
                  md: '0.875rem' // Desktop: body2 size
                },
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                padding: { xs: '2px 4px', sm: 0 },
                borderRadius: { xs: '4px', sm: 0 },
                transition: 'all 0.2s ease',
                maxWidth: { xs: '100px', sm: 'none' },
                overflow: { xs: 'hidden', sm: 'visible' },
                textOverflow: { xs: 'ellipsis', sm: 'initial' },
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: { xs: 'rgba(25, 118, 210, 0.08)', sm: 'transparent' },
                  transform: { xs: 'scale(1.05)', sm: 'none' }
                },
                '&:focus': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '1px'
                }
              }}
            >
              {isFirst && showHomeIcon && (
                <HomeIcon
                  sx={{
                    mr: 0.5,
                    fontSize: { xs: '0.9rem', sm: 'inherit' }
                  }}
                />
              )}
              {displayName}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default CustomBreadcrumb;
